import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    type DocsVerificationSideEnum,
    DocsVerificationFileSizeEnum,
    DocsVerificationTabEnum,
    DocsVerificationTypeEnum,
} from '@models/enums';
import type { FaceDetectResult, FileWithPreview } from '@models/types';
import { FileErrorCodeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/types/file-error-code-enum';
import type { UploadDocumentRequest } from '@modules/docs-verification-widget/api';
import {
    useUploadDocumentSDKMutation,
    useUploadLivenessDocumentSDKMutation,
} from '@modules/docs-verification-widget/api';
import { uploadFileResponse } from '@modules/docs-verification-widget/demo-responses/upload-file-response';
import type { UploadDocumentResponse } from '@modules/docs-verification-widget/models/upload-document-response';
import type { RootState } from '@store/root-reducer';
import type { VerificationFileState } from '@store/slices/docs-verification/docs-verification-slice';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { isValidExtension } from '@utils/files/util';
import { isFileSizeInRange, sleep } from '@utils/util';
import imageCompression from 'browser-image-compression';
import { v4 as uuidv4 } from 'uuid';

type UploadFileProps = {
    file: FileWithPreview | FileWithPreview[];
    tab: DocsVerificationTabEnum;
    documentType: DocsVerificationTypeEnum;
    faceDetectResult?: FaceDetectResult;
    side?: DocsVerificationSideEnum;
};

const validateFileSize = (size: number, minSize: number): VerificationFileState['status'] => {
    const resSizeFile = isFileSizeInRange(size, minSize);

    if (resSizeFile === DocsVerificationFileSizeEnum.SUCCESS) {
        return {
            error: false,
            success: false,
            message: '',
            loading: false,
        };
    }
    if (resSizeFile === DocsVerificationFileSizeEnum.BIG) {
        return {
            success: false,
            error: true,
            message: 'validation.invalidDocSizeSmaller',
            loading: false,
        };
    }

    return {
        error: true,
        success: false,
        message: 'validation.invalidDocSizeBigger',
        loading: false,
    };
};

const getSizeFile = (file: FileWithPreview, tab: DocsVerificationTabEnum) => {
    const minSize = tab === DocsVerificationTabEnum.ADDRESS ? 1 : 50;
    const status = validateFileSize(file.size, minSize);

    return {
        file,
        status,
    };
};

export const useUploadFile = (allowedExtensions?: string[]) => {
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const [uploadDocumentSdk] = useUploadDocumentSDKMutation();
    const [uploadLivenessDocumentSdk] = useUploadLivenessDocumentSDKMutation();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const uploadFile = useCallback(
        async ({
            file: filesToUpload,
            documentType,
            tab,
            side,
        }: UploadFileProps): Promise<VerificationFileState> => {
            const id = uuidv4();
            const file = Array.isArray(filesToUpload) ? filesToUpload[0] : filesToUpload;

            try {
                const res = getSizeFile(file as FileWithPreview, tab);

                if (allowedExtensions) {
                    const isValid = isValidExtension(file.name, allowedExtensions);

                    if (!isValid) {
                        const newFile = {
                            id,
                            tab,
                            uploadedFile: {
                                name: file.name,
                                preview: file.preview,
                                size: file.size,
                            },
                            documentType: '',
                            side: '',
                            status: {
                                error: true,
                                success: false,
                                message: t('validation.invalidFileExtension'),
                                loading: false,
                            },
                        } as unknown as VerificationFileState;

                        dispatch(docsVerificationSlice.actions.addFile(newFile));

                        return newFile;
                    }
                }

                if (res.status.error) {
                    const newFile = {
                        id,
                        tab,
                        uploadedFile: {
                            name: file.name,
                            preview: file.preview,
                            size: file.size,
                        },
                        documentType: '',
                        side: '',
                        status: res.status,
                    } as unknown as VerificationFileState;

                    dispatch(docsVerificationSlice.actions.addFile(newFile));

                    return newFile;
                }

                const newFile = {
                    id,
                    tab,
                    uploadedFile: {
                        name: file.name,
                        preview: file.preview,
                        size: file.size,
                    },
                    documentType: '',
                    side: '',
                    status: {
                        loading: true,
                        success: false,
                        error: false,
                    },
                } as unknown as VerificationFileState;

                dispatch(docsVerificationSlice.actions.addFile(newFile));

                let response: UploadDocumentResponse;

                if (sdkOptions?.isDemo) {
                    await sleep(1000);
                    response = uploadFileResponse({ documentType });
                } else if (documentType === DocsVerificationTypeEnum.LIVENESS) {
                    const files = filesToUpload as File[];

                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                    };

                    let compressed = files;

                    try {
                        compressed = await Promise.all(
                            files.map((item) =>
                                imageCompression(item, options).then(
                                    (compressedFile) => compressedFile,
                                ),
                            ),
                        );
                    } catch (e) {
                        /* empty */
                    }

                    response = await uploadLivenessDocumentSdk({
                        url: sdkOptions?.apiUrl as string,
                        id: sdkOptions?.id as string,
                        body: {
                            files: compressed,
                        },
                    }).unwrap();
                } else {
                    const body: UploadDocumentRequest['body'] = {
                        file: file as FileWithPreview,
                        // @ts-expect-error test
                        document_type: documentType,
                        side,
                    };

                    response = await uploadDocumentSdk({
                        url: sdkOptions?.apiUrl as string,
                        id: sdkOptions?.id as string,
                        body,
                    }).unwrap();
                }

                const uploadedFileState = {
                    id: response.document_id,
                    tab,
                    uploadedFile: {
                        name: file.name,
                        preview: file.preview as string,
                        size: file.size,
                    },
                    documentType: response.detected_document_type,
                    side: response.detected_document_side,
                    isTwoSideDocument: response.detected_two_side_document,
                    country: response.detected_country,
                    status: {
                        loading: false,
                        success: true,
                        error: false,
                    },
                };

                setTimeout(() => {
                    dispatch(
                        docsVerificationSlice.actions.changeFile({
                            id: newFile.id as string,
                            file: uploadedFileState,
                        }),
                    );
                });

                return uploadedFileState;
            } catch (error) {
                let message = '';
                let code = '';

                if (Array.isArray(error?.data?.errors)) {
                    message = error.data.errors[0]?.message || 'common.pleaseTryAgainLater';
                    code = error.data.errors[0]?.code || undefined;
                } else {
                    message = error?.data?.message;
                }

                const status = {
                    success: false,
                    loading: false,
                    error: true,
                    message: message || error?.error,
                    code,
                };

                const fileState = {
                    id,
                    uploadedFile: {
                        name: file?.name || 'file',
                        preview: file?.preview,
                        size: file?.size,
                    },
                    documentType: '',
                    side: '',
                    tab,
                    status,
                } as unknown as VerificationFileState;

                if (message?.includes(FileErrorCodeEnum.LIMIT_UPLOADING)) {
                    const successFileState = {
                        ...fileState,
                    };

                    successFileState.status.error = false;
                    successFileState.status.success = true;
                    successFileState.status.message = '';
                    successFileState.status.isFailedVerification = true;

                    dispatch(
                        docsVerificationSlice.actions.changeFile({
                            id,
                            file: successFileState,
                        }),
                    );

                    dispatch(docsVerificationSlice.actions.setIsFailedVerification(true));

                    return successFileState;
                }

                dispatch(
                    docsVerificationSlice.actions.changeFile({
                        id,
                        file: fileState,
                    }),
                );

                return fileState;
            }
        },
        [sdkOptions, uploadDocumentSdk, dispatch, t, allowedExtensions, uploadLivenessDocumentSdk],
    );

    return { uploadFile };
};
